import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Rocket, FileText, Bot, TrendingUp, Lightbulb } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { USE_CASES } from "@/data/content";

const iconMap = {
    "launch-brand": Rocket,
    "scale-content": FileText,
    "deploy-assistants": Bot,
    "demand-engines": TrendingUp,
    "prototype-ideas": Lightbulb,
};

export const UseCases = () => {
    return (
        <section
            id="use-cases"
            className="relative py-24 lg:py-32 bg-[hsl(var(--bg-2))]/60 border-y border-black/[0.05]"
            aria-labelledby="use-cases-heading"
        >
            <div className="mx-auto max-w-7xl container-px">
                <SectionHeading
                    eyebrow="Use Cases"
                    title={
                        <>
                            Five product scenarios.{" "}
                            <span className="italic text-[hsl(var(--text-muted))]">One operating system.</span>
                        </>
                    }
                    subtitle="Every engagement is shaped to a specific outcome. Each scenario leaves an owned, measurable system behind."
                    className="mb-12"
                />
                <div
                    id="use-cases-heading"
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
                >
                    {USE_CASES.map((u, i) => {
                        const Icon = iconMap[u.id] || Rocket;
                        return (
                            <motion.div
                                key={u.id}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-10% 0px" }}
                                transition={{
                                    duration: 0.5,
                                    delay: i * 0.05,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                whileHover={{ y: -3 }}
                                data-testid="use-case-card"
                                className="group relative rounded-2xl border border-black/[0.07] bg-white p-6 lg:p-7 hover:border-black/15 hover:shadow-soft transition-all duration-300"
                            >
                                <div className="flex items-start justify-between">
                                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(37,99,235,0.10)] ring-1 ring-[rgba(37,99,235,0.30)] text-[hsl(var(--blue))]">
                                        <Icon className="h-4 w-4" />
                                    </span>
                                    <ArrowUpRight className="h-4 w-4 text-[hsl(var(--text-subtle))] transition-all duration-200 group-hover:text-[hsl(var(--text))] group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </div>
                                <h3 className="mt-5 font-display text-2xl text-[hsl(var(--text))]">
                                    {u.title}
                                </h3>
                                <p className="mt-2 text-[14px] leading-relaxed text-[hsl(var(--text-muted))]">
                                    {u.description}
                                </p>
                                <ul className="mt-4 pt-4 border-t border-black/[0.08] space-y-2">
                                    {u.outcomes.map((o) => (
                                        <li
                                            key={o}
                                            className="flex items-start gap-2 text-[13px] text-[hsl(var(--text-muted))]"
                                        >
                                            <span className="mt-1.5 h-1 w-1 rounded-full bg-[hsl(var(--gold))]" />
                                            {o}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default UseCases;
